/**
 * Customer Profile Drawer
 * Right-sliding drawer showing detailed customer information,
 * booking history, membership data, and patterns.
 * Aggregates data from both SPOTTS and manual bookings.
 */

import { CURRENCY_CODE } from '../../utils/currency';
import React, { Fragment, useState, useEffect } from 'react';
import {
  X,
  User,
  Phone,
  Mail,
  Calendar,
  Clock,
  MapPin,
  TrendingUp,
  AlertCircle,
  Crown,
  CreditCard,
  Repeat,
  ShieldCheck,
  ShieldOff,
  Pencil,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { vendorApi, CustomerProfileData } from '../../services/api';
import { format } from 'date-fns';

interface TrustSettings {
  payment_deadline_hours: number;
  max_active_reservations: number;
  notes: string;
}

interface TrustedEntryShape {
  id: number;
  payment_deadline_hours: number;
  max_active_reservations: number;
  notes: string | null;
}

interface CustomerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  venueId: number;
  customer: {
    name: string;
    email?: string | null;
    phone?: string | null;
    source: 'spotts' | 'manual';
  } | null;
  // Trust management (Spotts users only)
  userId?: number | null;
  trustedEntry?: TrustedEntryShape | null;
  onAddTrust?: (settings: TrustSettings) => void;
  onUpdateTrust?: (entryId: number, settings: TrustSettings) => void;
  onRemoveTrust?: (entryId: number) => void;
  isAddTrustPending?: boolean;
  isUpdateTrustPending?: boolean;
  isRemoveTrustPending?: boolean;
}

export default function CustomerProfileModal({
  isOpen,
  onClose,
  venueId,
  customer,
  userId,
  trustedEntry,
  onAddTrust,
  onUpdateTrust,
  onRemoveTrust,
  isAddTrustPending,
  isUpdateTrustPending,
  isRemoveTrustPending,
}: CustomerProfileModalProps) {
  // Trust mode: 'view' (default), 'add', 'edit'
  const [trustMode, setTrustMode] = useState<'view' | 'add' | 'edit'>('view');
  const [trustForm, setTrustForm] = useState<TrustSettings>({
    payment_deadline_hours: 48,
    max_active_reservations: 2,
    notes: '',
  });

  // Reset trust UI whenever the trusted entry changes (e.g. after successful mutation)
  useEffect(() => {
    setTrustMode('view');
  }, [trustedEntry?.id]);

  // Pre-fill form when entering edit mode
  const enterEditMode = () => {
    if (trustedEntry) {
      setTrustForm({
        payment_deadline_hours: trustedEntry.payment_deadline_hours,
        max_active_reservations: trustedEntry.max_active_reservations,
        notes: trustedEntry.notes ?? '',
      });
    }
    setTrustMode('edit');
  };

  const handleTrustSave = () => {
    if (trustMode === 'add') {
      onAddTrust?.(trustForm);
    } else if (trustMode === 'edit' && trustedEntry) {
      onUpdateTrust?.(trustedEntry.id, trustForm);
    }
  };

  const cancelTrustMode = () => {
    setTrustMode('view');
    setTrustForm({ payment_deadline_hours: 48, max_active_reservations: 2, notes: '' });
  };

  const isTrustFormPending = trustMode === 'add' ? isAddTrustPending : isUpdateTrustPending;

  // Fetch customer profile data
  const { data: profile, isPending, error } = useQuery<CustomerProfileData>({
    queryKey: ['customerProfile', venueId, customer?.name, customer?.email, customer?.source],
    queryFn: () => vendorApi.getCustomerProfile(venueId, {
      name: customer!.name,
      email: customer?.email || undefined,
      phone: customer?.phone || undefined,
      source: customer!.source,
    }),
    enabled: isOpen && !!customer && !!venueId,
    staleTime: 5 * 60 * 1000,
  });

  if (!customer) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: CURRENCY_CODE,
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDateStr = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const membershipStats = profile?.membership_stats;
  const memberships = profile?.memberships || [];

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </TransitionChild>

        {/* Centered modal container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="w-full max-w-lg bg-white shadow-2xl rounded-2xl flex flex-col max-h-[90vh]">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10 rounded-t-2xl">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg flex-shrink-0 ${customer.source === 'spotts' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                    <User className={`w-5 h-5 ${customer.source === 'spotts' ? 'text-blue-600' : 'text-gray-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-gray-900 truncate">{customer.name}</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        customer.source === 'spotts'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {customer.source === 'spotts' ? 'SPOTTS User' : 'Walk-in'}
                      </span>
                      {membershipStats?.has_active_membership && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">
                          <Crown className="w-3 h-3" />
                          Active Member
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors flex-shrink-0"
                  aria-label="Close customer profile"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {isPending ? (
                  <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
                    <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
                    <p className="mt-4 text-sm text-gray-500">Loading customer profile...</p>
                  </div>
                ) : error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-700">Failed to load customer profile.</span>
                  </div>
                ) : profile ? (
                  <>
                    {/* Contact Info */}
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex flex-wrap gap-4">
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{customer.phone}</span>
                          </div>
                        )}
                        {customer.email && (
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{customer.email}</span>
                          </div>
                        )}
                        {!customer.phone && !customer.email && (
                          <span className="text-gray-500 italic text-sm">No contact information</span>
                        )}
                      </div>
                      {profile.customer.first_booking_date && (
                        <p className="text-xs text-gray-500 mt-2">
                          Customer since {formatDateStr(profile.customer.first_booking_date)}
                        </p>
                      )}
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <div className="text-xl font-bold text-green-700">{profile.stats.total_bookings}</div>
                        <div className="text-xs text-green-600">Bookings</div>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center overflow-hidden">
                        <div className="text-base font-bold text-blue-700 truncate" title={formatCurrency(profile.stats.total_spent)}>
                          {formatCurrency(profile.stats.total_spent)}
                        </div>
                        <div className="text-xs text-blue-600">Total Spent</div>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center overflow-hidden">
                        <div className="text-base font-bold text-purple-700 truncate" title={
                          profile.stats.last_visit_date
                            ? formatDateStr(profile.stats.last_visit_date)
                            : profile.stats.next_booking_date
                            ? formatDateStr(profile.stats.next_booking_date)
                            : 'N/A'
                        }>
                          {profile.stats.last_visit_date
                            ? formatDateStr(profile.stats.last_visit_date)
                            : profile.stats.next_booking_date
                            ? formatDateStr(profile.stats.next_booking_date)
                            : 'N/A'}
                        </div>
                        <div className="text-xs text-purple-600">
                          {profile.stats.last_visit_date
                            ? 'Last Visit'
                            : profile.stats.next_booking_date
                            ? 'Next Booking'
                            : 'No Visits'}
                        </div>
                      </div>
                    </div>

                    {/* Additional Stats Row */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Avg. Booking Value</div>
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {formatCurrency(profile.stats.avg_booking_value)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">Cancellation Rate</div>
                        <div className={`text-sm font-semibold ${
                          profile.stats.cancellation_rate > 20 ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {profile.stats.cancellation_rate}%
                          {profile.stats.cancelled_bookings > 0 && (
                            <span className="text-xs font-normal text-gray-500 ml-1">
                              ({profile.stats.cancelled_bookings})
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Trusted Reservations Section — Spotts users only */}
                    {customer.source === 'spotts' && userId && (
                      <div className={`rounded-lg border overflow-hidden ${trustedEntry ? 'border-green-200' : 'border-dashed border-gray-200'}`}>
                        {/* Section header */}
                        <div className={`px-3 py-2 flex items-center justify-between ${trustedEntry ? 'bg-green-50 border-b border-green-200' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <ShieldCheck className={`w-4 h-4 ${trustedEntry ? 'text-green-600' : 'text-gray-400'}`} />
                            <span className={`text-sm font-bold ${trustedEntry ? 'text-green-800' : 'text-gray-600'}`}>
                              Trusted Reservations
                            </span>
                            {trustedEntry && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-600 text-white">ACTIVE</span>
                            )}
                          </div>
                          {/* Action buttons (view mode only) */}
                          {trustMode === 'view' && trustedEntry && (
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={enterEditMode}
                                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors px-2 py-1 rounded-lg hover:bg-blue-50"
                              >
                                <Pencil className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => onRemoveTrust?.(trustedEntry.id)}
                                disabled={isRemoveTrustPending}
                                className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-red-600 transition-colors px-2 py-1 rounded-lg hover:bg-red-50 disabled:opacity-50"
                              >
                                <ShieldOff className="w-3 h-3" />
                                Remove
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="px-3 py-3">
                          {/* VIEW MODE — not trusted */}
                          {trustMode === 'view' && !trustedEntry && (
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-xs text-gray-500 leading-relaxed">
                                Allow this customer to hold slots before paying.
                              </p>
                              <button
                                onClick={() => setTrustMode('add')}
                                className="flex-shrink-0 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-lg hover:bg-green-100 transition-colors"
                              >
                                Set up
                              </button>
                            </div>
                          )}

                          {/* VIEW MODE — trusted */}
                          {trustMode === 'view' && trustedEntry && (
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Payment deadline</span>
                                <div className="font-semibold text-gray-900 mt-0.5">{trustedEntry.payment_deadline_hours}h before booking</div>
                              </div>
                              <div>
                                <span className="text-gray-500">Max reservations</span>
                                <div className="font-semibold text-gray-900 mt-0.5">{trustedEntry.max_active_reservations} slots at a time</div>
                              </div>
                              {trustedEntry.notes && (
                                <div className="col-span-2">
                                  <span className="text-gray-500">Notes</span>
                                  <div className="font-medium text-gray-700 mt-0.5">{trustedEntry.notes}</div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ADD / EDIT FORM */}
                          {(trustMode === 'add' || trustMode === 'edit') && (
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">Payment deadline</label>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number" min={1} max={720}
                                      value={trustForm.payment_deadline_hours}
                                      onChange={e => setTrustForm(p => ({ ...p, payment_deadline_hours: Math.max(1, +e.target.value) }))}
                                      className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    <span className="text-xs text-gray-500">hrs</span>
                                  </div>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-700 mb-1">Max reservations</label>
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      type="number" min={1} max={10}
                                      value={trustForm.max_active_reservations}
                                      onChange={e => setTrustForm(p => ({ ...p, max_active_reservations: Math.max(1, +e.target.value) }))}
                                      className="w-16 border border-gray-200 rounded-lg px-2 py-1.5 text-sm font-semibold text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                                    />
                                    <span className="text-xs text-gray-500">slots</span>
                                  </div>
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 mb-1">Notes <span className="font-normal text-gray-400">(optional)</span></label>
                                <input
                                  type="text" maxLength={500}
                                  value={trustForm.notes}
                                  onChange={e => setTrustForm(p => ({ ...p, notes: e.target.value }))}
                                  placeholder="e.g. Regular customer, always pays on time"
                                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                              <div className="flex gap-2 pt-1">
                                <button
                                  onClick={cancelTrustMode}
                                  disabled={isTrustFormPending}
                                  className="flex-1 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleTrustSave}
                                  disabled={isTrustFormPending}
                                  className="flex-1 py-1.5 text-xs font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
                                >
                                  {isTrustFormPending ? (
                                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                  ) : (
                                    <ShieldCheck className="w-3.5 h-3.5" />
                                  )}
                                  {isTrustFormPending ? 'Saving...' : trustMode === 'add' ? 'Enable' : 'Save'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Membership Section */}
                    {membershipStats && membershipStats.total_memberships > 0 && (
                      <div className="border border-green-200 rounded-lg overflow-hidden">
                        <div className="bg-green-50 px-3 py-2 border-b border-green-200 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Crown className="w-4 h-4 text-green-600" />
                            <h3 className="text-sm font-bold text-green-800">Memberships</h3>
                          </div>
                          <span className="text-xs font-semibold text-green-600">
                            {formatCurrency(membershipStats.total_membership_spend)} total
                          </span>
                        </div>

                        {/* Active membership banner */}
                        {membershipStats.has_active_membership && membershipStats.current_package && (
                          <div className="px-3 py-2 bg-green-50/50 border-b border-green-100 flex items-center justify-between">
                            <div>
                              <span className="text-xs font-bold text-green-700">{membershipStats.current_package}</span>
                              <span className="text-xs text-green-600 ml-1">(Active)</span>
                            </div>
                            {membershipStats.days_remaining !== null && (
                              <span className="text-xs text-green-600">
                                {membershipStats.days_remaining} days left
                              </span>
                            )}
                          </div>
                        )}

                        {/* Membership history */}
                        <div className="divide-y divide-gray-100 max-h-40 overflow-y-auto">
                          {memberships.map((m) => (
                            <div key={m.id} className="px-3 py-2 flex items-center justify-between">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="font-medium text-gray-900">{m.package_name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    m.status === 'active'
                                      ? 'bg-green-100 text-green-700'
                                      : m.status === 'expired'
                                      ? 'bg-gray-100 text-gray-600'
                                      : 'bg-red-100 text-red-600'
                                  }`}>
                                    {m.status}
                                  </span>
                                </div>
                                <div className="text-[11px] text-gray-500 mt-0.5">
                                  {formatDateStr(m.start_date)} — {formatDateStr(m.end_date)}
                                </div>
                              </div>
                              <div className="text-xs font-semibold text-gray-900 ml-2">
                                {formatCurrency(m.price)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Booking Source Breakdown */}
                    {(profile.stats.source_breakdown.spotts > 0 || profile.stats.source_breakdown.manual > 0) && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-700 mb-2">Booking Sources</div>
                        <div className="flex flex-wrap gap-3">
                          {profile.stats.source_breakdown.spotts > 0 && (
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                              <span className="text-xs text-gray-600">
                                SPOTTS: {profile.stats.source_breakdown.spotts}
                              </span>
                            </div>
                          )}
                          {profile.stats.source_breakdown.manual > 0 && (
                            <div className="flex items-center gap-2">
                              <div className="w-2.5 h-2.5 rounded-full bg-gray-500"></div>
                              <span className="text-xs text-gray-600">
                                Walk-in: {profile.stats.source_breakdown.manual}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Patterns */}
                    {(profile.patterns.preferred_day || profile.patterns.preferred_time || profile.patterns.preferred_court) && (
                      <div className="border border-gray-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-gray-400" />
                          <h3 className="text-sm font-medium text-gray-900">Booking Patterns</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {profile.patterns.preferred_day && (
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-600">Day:</span>
                              <span className="font-medium text-gray-900">{profile.patterns.preferred_day}s</span>
                            </div>
                          )}
                          {profile.patterns.preferred_time && (
                            <div className="flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-600">Time:</span>
                              <span className="font-medium text-gray-900">{profile.patterns.preferred_time}</span>
                            </div>
                          )}
                          {profile.patterns.preferred_court && (
                            <div className="flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-600">Court:</span>
                              <span className="font-medium text-gray-900 truncate">{profile.patterns.preferred_court}</span>
                            </div>
                          )}
                          {profile.patterns.preferred_sport && (
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-600">Sport:</span>
                              <span className="font-medium text-gray-900">{profile.patterns.preferred_sport}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment Methods */}
                    {Object.keys(profile.stats.payment_methods).length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-4 h-4 text-gray-400" />
                          <span className="text-xs font-medium text-gray-700">Payment Methods</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(profile.stats.payment_methods).map(([method, count]) => (
                            <span key={method} className="px-2 py-1 bg-white border border-gray-200 rounded-full text-xs">
                              {method === 'cash' && '💵'}
                              {method === 'transfer' && '🏦'}
                              {method === 'pos' && '💳'}
                              {' '}{method.charAt(0).toUpperCase() + method.slice(1)}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Booking History */}
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900">Booking History</h3>
                        <span className="text-xs text-gray-500">
                          {profile.bookings.length}/{profile.total_bookings_count}
                        </span>
                      </div>

                      {profile.bookings.length > 0 ? (
                        <div className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
                          {profile.bookings.map((booking) => (
                            <div key={booking.id} className="px-3 py-2 hover:bg-gray-50 transition-colors">
                              <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 text-xs flex-wrap">
                                    <span className="font-medium text-gray-900">
                                      {formatDateStr(booking.booking_date)}
                                    </span>
                                    <span className="text-gray-500">
                                      {formatTime(booking.start_time)}-{formatTime(booking.end_time)}
                                    </span>
                                    <span className={`text-[10px] px-1 py-0.5 rounded ${
                                      booking.source === 'spotts'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {booking.source === 'spotts' ? '🔄' : '📋'}
                                    </span>
                                    {booking.recurring_group_id && (
                                      <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium" title={`Recurring: ${booking.recurring_group_id}`}>
                                        <Repeat className="w-2.5 h-2.5" />
                                        {booking.recurring_group_id}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-gray-500 truncate">
                                    {booking.court_name} • {booking.sport_name}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div className="text-xs font-medium text-gray-900">
                                    {formatCurrency(booking.amount)}
                                  </div>
                                  <div className={`text-[10px] px-1.5 py-0.5 rounded-full inline-block ${
                                    booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    booking.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                                    booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {booking.status}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="px-4 py-6 text-center text-gray-500 text-xs">
                          No booking history found
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              {/* Footer */}
              <div className="sticky bottom-0 border-t border-gray-200 px-4 py-3 bg-gray-50 rounded-b-2xl">
                <button
                  onClick={onClose}
                  className="w-full px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
